// Reads AI/admin slot assignments and resolves each to its full Photo record.
// Components call useSlotPhotos("home-gallery") and get back ordered photos —
// no more runtime randomization.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Photo } from "@/lib/photoLibrary";

export interface SlotAssignment {
  slot_key: string;
  position: number;
  photo_id: string;
  assigned_by: "ai" | "admin";
  rationale: string | null;
  locked: boolean;
  photo: Photo;
}

const PHOTO_FIELDS =
  "id, public_url, alt_text, caption, tags, placements, width, height, usage_count, is_hero_candidate, media_type, poster_public_url, duration_seconds, approved_at, exclude_from_gallery, status";

export const useSlotPhotos = (slotKey: string) =>
  useQuery({
    queryKey: ["slot-photos", slotKey],
    queryFn: async (): Promise<Photo[]> => {
      const { data, error } = await supabase
        .from("slot_assignments")
        .select(`position, photo:photos(${PHOTO_FIELDS})`)
        .eq("slot_key", slotKey)
        .order("position", { ascending: true });
      if (error) {
        console.error("useSlotPhotos", slotKey, error);
        return [];
      }
      return (data ?? [])
        .map((row) => (row as { photo: (Photo & { status?: string }) | null }).photo)
        .filter((p): p is Photo =>
          Boolean(
            p &&
            p.public_url &&
            p.status === "approved" &&
            p.approved_at &&
            p.exclude_from_gallery === false,
          ),
        );
    },
    staleTime: 60_000,
  });

/** Convenience: returns the first photo for slots that hold exactly one. */
export const useSlotPhoto = (slotKey: string) => {
  const q = useSlotPhotos(slotKey);
  return { ...q, data: q.data?.[0] };
};
